import { MOSIDA_API_BASE_URL } from "../config/apiUrls";
import type { AiSelfieCategory } from "./aiSelfieCategoryService";
import type { Product } from "./productService";

const apiBase = MOSIDA_API_BASE_URL;

export interface AiSelfieArt {
  id: string;
  category_id: string;
  /** Populated when the API includes nested category data. */
  category?: Partial<AiSelfieCategory> | null;
  name: string;
  /** Optional background image URL to pass through to generation APIs as `background`. */
  background_image?: string | null;
  home_bg_image?: string | null;
  inner_bg_image?: string | null;
  /** Style reference for `generate-reference-style-web` when set; otherwise `image` / `image_url` is used. */
  reference_image?: string | null;
  image?: string | null;
  image_url?: string | null;
  /** Station logo shown on themed flow screens. */
  logo?: string | null;
  logo_url?: string | null;
  /** Primary generation prompt from API (replaces prompts.json when set). */
  prompt?: string | null;
  negative_prompt?: string | null;
  /**
   * When true, use caricature station preset and Caricature prompt fallback if `prompt` is empty
   * (hobby-style flow from admin without an inline prompt).
   */
  is_caricature_hobby?: boolean | number | string | null;
  /**
   * When true, route through FIFA World Cup flow (select card → upload → profile, etc.).
   */
  is_fifa?: boolean | number | string | null;
  /**
   * Extra fields for `/api/generate-character` (object or JSON string).
   * May override canvas_size, remove_bg, background_url, position, etc.
   * Other keys are appended to the multipart form as-is (objects JSON-stringified).
   */
  additional_params?: Record<string, unknown> | string | null;
  status: boolean;
  /** Optional creativity control value from active arts API. */
  temperature?: number | string | null;
  /** Backward-compatible typo variant seen in some payloads. */
  temparature?: number | string | null;
  /** Optional logo placement from AI Station API payload. */
  logo_position?: string | null;
  /** Backward-compatible typo variant seen in some payloads. */
  logo_postition?: string | null;
  /** Onsite print products linked to this art (from active arts API). */
  onsite_products?: Product[] | null;
  sort?: number | null;
  created_at: string;
  updated_at: string;
}

function getHeaders(): HeadersInit {
  return {
    accept: "*/*",
  };
}

export function aiSelfieArtImageUrl(art: Partial<AiSelfieArt>): string {
  return art.image || art.image_url || "";
}

/** Strict reference URL for switching API paths: only `reference_image` qualifies. */
export function aiSelfieArtStrictReferenceImageUrl(art: Partial<AiSelfieArt>): string {
  return typeof art.reference_image === "string" ? art.reference_image.trim() : "";
}

/** URL for the reference-style API: prefers `reference_image`, then `image` / `image_url`. */
export function aiSelfieArtReferenceImageUrl(art: Partial<AiSelfieArt>): string {
  const ref = aiSelfieArtStrictReferenceImageUrl(art);
  if (ref) return ref;
  return aiSelfieArtImageUrl(art);
}

export async function getActiveAiSelfieArtsByCategory(categoryId: string): Promise<{
  success: boolean;
  data: AiSelfieArt[];
  error?: string;
}> {
  if (!categoryId) {
    return { success: false, data: [], error: "Category ID is required" };
  }

  try {
    const params = new URLSearchParams({ category_id: categoryId });
    const response = await fetch(`${apiBase}/ai-selfie-arts/active?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(),
    });

    if (!response.ok) {
      const text = await response.text();
      return {
        success: false,
        data: [],
        error: `Failed to load AI selfie arts: ${response.status} ${text}`,
      };
    }

    const json = await response.json();
    const data = Array.isArray(json?.data) ? json.data : [];
    return {
      success: Boolean(json?.success !== false),
      data,
      error: json?.message,
    };
  } catch (error: any) {
    return {
      success: false,
      data: [],
      error: error?.message || "Network error while loading AI selfie arts",
    };
  }
}
